import { useState } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import viaCepApi from 'services/viaCepApi'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import addressValidator from 'validators/addressValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'

import {
  Form,
  AddressContainer,
  FormField,
  Input,
  MaskedInput,
  Row,
  WarningText,
  LoadingBackdrop,
  ErrorContainer
} from './styles'

export default function EditMemberAddressModal ({
  visible,
  data: member,
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [loadingAddress, setLoadingAddress] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const token = AuthService.getToken ()
  
  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const searchAddress = async (event, setFieldValue) => {
    const postal_code = event.target.rawValue

    setFieldValue ('postal_code', postal_code)
    
    if (postal_code.length === 8) {
      setLoadingAddress (true)
      try {
        const { data } = await viaCepApi.get (`/ws/${postal_code}/json`)

        setFieldValue ('route', data.logradouro)
        setFieldValue ('district', data.bairro)
        setFieldValue ('city', data.localidade)
        setFieldValue ('state', data.uf)
      } catch (error) {
        logApiError (error)
      } finally {
        setLoadingAddress (false)
      }
    }
  }

  if (!member) return null
    
  const address = member.addresses[0]

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='lg' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Editar endereço do membro</DialogTitle>
        </DialogHeader>
        <Formik
          validationSchema={addressValidator}
          initialValues={{
            'id': address.id,
            'route': address.route,
            'number': String (address.number),
            'complement': address.complement,
            'district': address.district,
            'city': address.city,
            'state': address.state,
            'postal_code': address.postal_code
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              setSubmitting (true)
              const address = values

              const { data: updatedMember } = await cutileiApi.put (
                `/members/${member.id}`, {address}, requestConfig
              )
            
              setSubmitting (false)
              handleConfirm (updatedMember)
              handleClose ()
            } catch (error) {
              setSubmitting (false)
              logApiError (error)
              setErrorMessage (
                error.response?.data?.message ?? 'Erro ao editar o endereço do membro. Tente novamente.'
              )
            }
          }}
        >
          {function EditMemberAddressForm ({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            handleChange,
            handleBlur, 
            handleSubmit
          }) {
            return (
              <>
                <DialogBody>
                  <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
                    <AddressContainer>
                      {loadingAddress && (
                        <LoadingBackdrop>
                          <Loading/>
                        </LoadingBackdrop>
                      )}
                      <Row>
                        <FormField>
                          <MaskedInput
                            name='postal_code'
                            placeholder='CEP'
                            options={{
                              blocks: [5,3],
                              delimiters: ['-'],
                              numericOnly: true
                            }}
                            value={values.postal_code}
                            onChange={event => searchAddress (event, setFieldValue)}
                            onBlur={handleBlur ('postal_code')}
                          />
                          {touched.postal_code && errors.postal_code && (
                            <WarningText>
                              {errors.postal_code}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField stretch={2}>
                          <Input
                            placeholder='Rua/avenida'
                            value={values.route}
                            onChange={handleChange ('route')}
                            onBlur={handleBlur ('route')}
                            disabled
                          />
                        </FormField>
                        <FormField>
                          <Input
                            placeholder='Número'
                            value={values.number}
                            onChange={handleChange ('number')}
                            onBlur={handleBlur ('number')}
                          />
                          {touched.number && errors.number && (
                            <WarningText>
                              {errors.number}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField>
                          <Input
                            placeholder='Complemento'
                            value={values.complement}
                            onChange={handleChange ('complement')}
                            onBlur={handleBlur ('complement')}
                          />
                        </FormField>
                      </Row>
                      <Row>
                        <FormField stretch={4}>
                          <Input
                            placeholder='Bairro'
                            value={values.district}
                            onChange={handleChange ('district')}
                            onBlur={handleBlur ('district')}
                            disabled
                          />
                        </FormField>
                        <FormField stretch={3}>
                          <Input
                            placeholder='Cidade'
                            value={values.city}
                            onChange={handleChange ('city')}
                            onBlur={handleBlur ('city')}
                            disabled
                          />
                        </FormField>
                        <FormField>
                          <Input
                            placeholder='Estado'
                            value={values.state}
                            onBlur={handleBlur ('state')}
                            disabled
                          />
                        </FormField>
                      </Row>
                    </AddressContainer>
                    {errorMessage && (
                      <ErrorContainer>
                        <p>{errorMessage}</p>
                      </ErrorContainer>
                    )}
                  </Form>
                </DialogBody>
                <DialogFooter>
                  <MainButton
                    label='Salvar alterações'
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    width={160}
                  />
                </DialogFooter>
              </>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
